/**
 * Pick the most natural available browser TTS voice (interim until server TTS).
 * Prefers cloud/neural/enhanced en-US voices; avoids legacy robotic desktop voices.
 */

const PREFERRED_VOICE_NAME_PATTERNS = [
  /microsoft aria/i,
  /microsoft jenny/i,
  /microsoft guy/i,
  /microsoft libby/i,
  /microsoft ana/i,
  /microsoft andrew/i,
  /google.*english.*(united states|us)/i,
  /google us english/i,
  /samantha/i,
  /karen/i,
  /daniel/i,
  /moira/i,
  /tessa/i,
  /enhanced/i,
  /natural/i,
  /neural/i,
  /premium/i,
];

const AVOID_VOICE_NAME_PATTERNS = [
  /espeak/i,
  /festival/i,
  /pico/i,
  /microsoft david desktop/i,
  /microsoft zira desktop/i,
  /microsoft mark desktop/i,
  /compact/i,
  /robot/i,
];

/** Slightly slower + steady pitch reads more naturally than default 1.0. */
export const GUIDED_TOUR_SPEECH_RATE = 0.93;
export const GUIDED_TOUR_SPEECH_PITCH = 1;
export const GUIDED_TOUR_SPEECH_LANG = "en-US";

let voicesReadyPromise = null;
let cachedPreferredVoice = null;

function isEnglishVoice(voice) {
  return (voice.lang || "").toLowerCase().startsWith("en");
}

function scoreVoice(voice) {
  if (!isEnglishVoice(voice)) return -1;

  const name = voice.name || "";
  let score = 0;

  for (const pattern of PREFERRED_VOICE_NAME_PATTERNS) {
    if (pattern.test(name)) score += 40;
  }

  if (/online/i.test(name)) score += 15;
  if (voice.localService === false) score += 8;

  if (/(united states|en-us|_us)/i.test(voice.lang || "")) score += 12;
  if (voice.default) score += 4;

  for (const pattern of AVOID_VOICE_NAME_PATTERNS) {
    if (pattern.test(name)) score -= 50;
  }

  return score;
}

function pickPreferredVoice(voices) {
  const envHint = process.env.REACT_APP_GUIDED_TOUR_SPEECH_VOICE?.trim();
  if (envHint) {
    const hinted = voices.find((v) =>
      (v.name || "").toLowerCase().includes(envHint.toLowerCase())
    );
    if (hinted) return hinted;
  }

  const ranked = voices
    .map((voice) => ({ voice, score: scoreVoice(voice) }))
    .filter(({ score }) => score >= 0)
    .sort((a, b) => b.score - a.score);

  if (ranked.length > 0) return ranked[0].voice;

  return voices.find(isEnglishVoice) || voices[0] || null;
}

export function getSpeechVoicesWhenReady() {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    return Promise.resolve([]);
  }

  if (voicesReadyPromise) return voicesReadyPromise;

  voicesReadyPromise = new Promise((resolve) => {
    const synth = window.speechSynthesis;

    const finish = () => {
      resolve(synth.getVoices());
    };

    const voices = synth.getVoices();
    if (voices.length > 0) {
      finish();
      return;
    }

    const onVoicesChanged = () => {
      synth.removeEventListener("voiceschanged", onVoicesChanged);
      finish();
    };

    synth.addEventListener("voiceschanged", onVoicesChanged);
    window.setTimeout(finish, 750);
  });

  return voicesReadyPromise;
}

export async function resolveGuidedTourSpeechVoice() {
  if (cachedPreferredVoice) return cachedPreferredVoice;

  const voices = await getSpeechVoicesWhenReady();
  cachedPreferredVoice = pickPreferredVoice(voices);
  return cachedPreferredVoice;
}

/** Split on sentence boundaries for clearer pacing on long step copy. */
export function splitTextForSpeech(text) {
  const trimmed = text.trim();
  if (!trimmed) return [];

  const parts = trimmed.split(/(?<=[.!?])\s+/).filter(Boolean);
  return parts.length > 0 ? parts : [trimmed];
}

export function configureSpeechUtterance(utterance, voice) {
  utterance.lang = GUIDED_TOUR_SPEECH_LANG;
  utterance.rate = GUIDED_TOUR_SPEECH_RATE;
  utterance.pitch = GUIDED_TOUR_SPEECH_PITCH;
  if (voice) utterance.voice = voice;
}
